'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function SquareFootageCalculator() {
  const t = useTranslations("tools.squareFootageCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Shape-specific states
  const [rectangleInputs, setRectangleInputs] = useState({
    length: 30,
    lengthUnit: 'feet',
    width: 20,
    widthUnit: 'feet',
    quantity: 1,
    price: 0
  });
  const [rectangleResults, setRectangleResults] = useState<any>(null);

  const [circleInputs, setCircleInputs] = useState({
    diameter: 30,
    diameterUnit: 'feet',
    quantity: 1,
    price: 0
  });
  const [circleResults, setCircleResults] = useState<any>(null);

  const [triangleInputs, setTriangleInputs] = useState({
    edge1: 30,
    edge1Unit: 'feet',
    edge2: 45,
    edge2Unit: 'feet',
    edge3: 50,
    edge3Unit: 'feet',
    quantity: 1,
    price: 0
  });
  const [triangleResults, setTriangleResults] = useState<any>(null);
  const [triangleError, setTriangleError] = useState('');

  const [borderInputs, setBorderInputs] = useState({
    length: 30,
    lengthUnit: 'feet',
    width: 20,
    widthUnit: 'feet',
    border: 2,
    borderUnit: 'feet',
    quantity: 1,
    price: 0
  });
  const [borderResults, setBorderResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const unitConversions: { [key: string]: number } = {
    feet: 1,
    yards: 3,
    inches: 1/12,
    meters: 3.28084,
    centimeters: 0.0328084
  };

  const convertToFeet = (value: number, unit: string): number => {
    return value * unitConversions[unit];
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const calculateResults = (sqFeet: number, quantity: number, price: number) => {
    const totalSqFeet = sqFeet * quantity;
    const sqYards = totalSqFeet / 9;
    const sqMeters = totalSqFeet / 10.7639;
    const totalCost = price > 0 ? totalSqFeet * price : null;

    return {
      sqFeet: totalSqFeet,
      sqYards,
      sqMeters,
      totalCost
    };
  };

  const handleRectangleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const lengthFeet = convertToFeet(rectangleInputs.length, rectangleInputs.lengthUnit);
    const widthFeet = convertToFeet(rectangleInputs.width, rectangleInputs.widthUnit);
    const sqFeet = lengthFeet * widthFeet;
    setRectangleResults(calculateResults(sqFeet, rectangleInputs.quantity, rectangleInputs.price));
  };

  const handleCircleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const diameterFeet = convertToFeet(circleInputs.diameter, circleInputs.diameterUnit);
    const radius = diameterFeet / 2;
    const sqFeet = Math.PI * radius * radius;
    setCircleResults(calculateResults(sqFeet, circleInputs.quantity, circleInputs.price));
  };

  const handleTriangleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const a = convertToFeet(triangleInputs.edge1, triangleInputs.edge1Unit);
    const b = convertToFeet(triangleInputs.edge2, triangleInputs.edge2Unit);
    const c = convertToFeet(triangleInputs.edge3, triangleInputs.edge3Unit);

    const s = (a + b + c) / 2;
    const sqFeet = Math.sqrt(s * (s - a) * (s - b) * (s - c));

    if (isNaN(sqFeet) || sqFeet <= 0) {
      setTriangleError(t('triangleError'));
      setTriangleResults(null);
      return;
    }

    setTriangleError('');
    setTriangleResults(calculateResults(sqFeet, triangleInputs.quantity, triangleInputs.price));
  };

  const handleBorderCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const lengthFeet = convertToFeet(borderInputs.length, borderInputs.lengthUnit);
    const widthFeet = convertToFeet(borderInputs.width, borderInputs.widthUnit);
    const borderFeet = convertToFeet(borderInputs.border, borderInputs.borderUnit);

    const outerArea = lengthFeet * widthFeet;
    const innerLength = lengthFeet - (2 * borderFeet);
    const innerWidth = widthFeet - (2 * borderFeet);
    const innerArea = Math.max(0, innerLength * innerWidth);
    const sqFeet = outerArea - innerArea;

    setBorderResults(calculateResults(sqFeet, borderInputs.quantity, borderInputs.price));
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const subtitleStyle = getResponsiveStyle(
    { color: colors.textSecondary, marginBottom: '0' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const shapesGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const shapeCardStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
      transition: 'transform 0.2s, box-shadow 0.2s'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const shapeHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1.25rem',
    paddingBottom: '1rem',
    borderBottom: '2px solid #e2e8f0'
  };

  const shapeIconStyle = getResponsiveStyle(
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      borderRadius: '0.75rem',
      color: 'white'
    },
    { width: '50px', height: '50px', fontSize: '1.25rem' },
    { width: '60px', height: '60px', fontSize: '1.5rem' },
    { width: '60px', height: '60px', fontSize: '1.5rem' }
  );

  const formGroupStyle = { marginBottom: '1rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500 },
    { fontSize: '0.875rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.9rem' }
  );

  const inputRowStyle = { display: 'flex', gap: '0.5rem', alignItems: 'center' };

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'border-color 0.2s'
    },
    { padding: '0.625rem', fontSize: '0.875rem' },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const selectStyle = {
    ...inputStyle,
    background: 'white',
    cursor: 'pointer'
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      marginTop: '0.5rem'
    },
    { padding: '0.75rem', fontSize: '0.875rem', minHeight: '44px' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
      marginTop: '1rem'
    },
    { padding: '1.25rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const resultItemStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    marginBottom: '0.5rem'
  };

  const resultLabelStyle = { color: '#64748b', fontWeight: 500 };

  const resultValueStyle = getResponsiveStyle(
    { color: colors.textPrimary, fontWeight: 700 },
    { fontSize: '1rem' },
    { fontSize: '1.1rem' },
    { fontSize: '1.1rem' }
  );

  const resultValueHighlightStyle = { ...resultValueStyle, color: '#2563eb' };

  const errorMessageStyle = getResponsiveStyle(
    {
      background: '#fef2f2',
      color: '#dc2626',
      borderRadius: '0.5rem',
      marginTop: '0.5rem',
      borderLeft: '4px solid #dc2626'
    },
    { padding: '0.625rem', fontSize: '0.875rem' },
    { padding: '0.75rem', fontSize: '0.9rem' },
    { padding: '0.75rem', fontSize: '0.9rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = { ...paragraphStyle, paddingLeft: '1.5rem' };

  const faqItemStyle = getResponsiveStyle(
    { marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' }
  );

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    wordBreak: 'break-word' as const
  };

  const renderResults = (results: any) => {
    if (!results) return null;

    return (
      <div style={resultsStyle}>
        <h4 style={h3Style}>{t('resultsTitle')}</h4>
        <div style={resultItemStyle}>
          <span style={resultLabelStyle}>{t('squareFeet')}</span>
          <span style={resultValueHighlightStyle}>{formatNumber(results.sqFeet)} ft²</span>
        </div>
        <div style={resultItemStyle}>
          <span style={resultLabelStyle}>{t('squareYards')}</span>
          <span style={resultValueStyle}>{formatNumber(results.sqYards)} yd²</span>
        </div>
        <div style={resultItemStyle}>
          <span style={resultLabelStyle}>{t('squareMeters')}</span>
          <span style={resultValueStyle}>{formatNumber(results.sqMeters)} m²</span>
        </div>
        {results.totalCost !== null && (
          <div style={resultItemStyle}>
            <span style={resultLabelStyle}>{t('totalCost')}</span>
            <span style={resultValueHighlightStyle}>${formatNumber(results.totalCost)}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={subtitleStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>{t('toolTitle')}</h2>
          <p style={subtitleStyle}>{t('toolSubtitle')}</p>
        </div>

        <div style={shapesGridStyle}>
          {/* Rectangle Calculator */}
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <h3 style={h3Style}>{t('rectangleTitle')}</h3>
              <div style={shapeIconStyle}>▭</div>
            </div>
            <form onSubmit={handleRectangleCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('length')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={rectangleInputs.length}
                    onChange={(e) => setRectangleInputs({...rectangleInputs, length: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={rectangleInputs.lengthUnit}
                    onChange={(e) => setRectangleInputs({...rectangleInputs, lengthUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('feet')}</option>
                    <option value="yards">{t('yards')}</option>
                    <option value="inches">{t('inches')}</option>
                    <option value="meters">{t('meters')}</option>
                    <option value="centimeters">{t('centimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('width')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={rectangleInputs.width}
                    onChange={(e) => setRectangleInputs({...rectangleInputs, width: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={rectangleInputs.widthUnit}
                    onChange={(e) => setRectangleInputs({...rectangleInputs, widthUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('feet')}</option>
                    <option value="yards">{t('yards')}</option>
                    <option value="inches">{t('inches')}</option>
                    <option value="meters">{t('meters')}</option>
                    <option value="centimeters">{t('centimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('quantity')}</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={rectangleInputs.quantity}
                  onChange={(e) => setRectangleInputs({...rectangleInputs, quantity: parseInt(e.target.value) || 1})}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('pricePerSqFt')}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={rectangleInputs.price || ''}
                  onChange={(e) => setRectangleInputs({...rectangleInputs, price: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  placeholder="0.00"
                />
              </div>
              <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
            </form>
            {renderResults(rectangleResults)}
          </div>

          {/* Circle Calculator */}
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <h3 style={h3Style}>{t('circleTitle')}</h3>
              <div style={shapeIconStyle}>○</div>
            </div>
            <form onSubmit={handleCircleCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('diameter')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={circleInputs.diameter}
                    onChange={(e) => setCircleInputs({...circleInputs, diameter: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={circleInputs.diameterUnit}
                    onChange={(e) => setCircleInputs({...circleInputs, diameterUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('feet')}</option>
                    <option value="yards">{t('yards')}</option>
                    <option value="inches">{t('inches')}</option>
                    <option value="meters">{t('meters')}</option>
                    <option value="centimeters">{t('centimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('quantity')}</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={circleInputs.quantity}
                  onChange={(e) => setCircleInputs({...circleInputs, quantity: parseInt(e.target.value) || 1})}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('pricePerSqFt')}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={circleInputs.price || ''}
                  onChange={(e) => setCircleInputs({...circleInputs, price: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  placeholder="0.00"
                />
              </div>
              <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
            </form>
            {renderResults(circleResults)}
          </div>

          {/* Triangle Calculator */}
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <h3 style={h3Style}>{t('triangleTitle')}</h3>
              <div style={shapeIconStyle}>△</div>
            </div>
            <form onSubmit={handleTriangleCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('edge1')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={triangleInputs.edge1}
                    onChange={(e) => setTriangleInputs({...triangleInputs, edge1: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={triangleInputs.edge1Unit}
                    onChange={(e) => setTriangleInputs({...triangleInputs, edge1Unit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('feet')}</option>
                    <option value="yards">{t('yards')}</option>
                    <option value="inches">{t('inches')}</option>
                    <option value="meters">{t('meters')}</option>
                    <option value="centimeters">{t('centimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('edge2')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={triangleInputs.edge2}
                    onChange={(e) => setTriangleInputs({...triangleInputs, edge2: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={triangleInputs.edge2Unit}
                    onChange={(e) => setTriangleInputs({...triangleInputs, edge2Unit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('feet')}</option>
                    <option value="yards">{t('yards')}</option>
                    <option value="inches">{t('inches')}</option>
                    <option value="meters">{t('meters')}</option>
                    <option value="centimeters">{t('centimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('edge3')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={triangleInputs.edge3}
                    onChange={(e) => setTriangleInputs({...triangleInputs, edge3: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={triangleInputs.edge3Unit}
                    onChange={(e) => setTriangleInputs({...triangleInputs, edge3Unit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('feet')}</option>
                    <option value="yards">{t('yards')}</option>
                    <option value="inches">{t('inches')}</option>
                    <option value="meters">{t('meters')}</option>
                    <option value="centimeters">{t('centimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('quantity')}</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={triangleInputs.quantity}
                  onChange={(e) => setTriangleInputs({...triangleInputs, quantity: parseInt(e.target.value) || 1})}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('pricePerSqFt')}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={triangleInputs.price || ''}
                  onChange={(e) => setTriangleInputs({...triangleInputs, price: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  placeholder="0.00"
                />
              </div>
              <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
            </form>
            {triangleError && (
              <div style={errorMessageStyle}>{triangleError}</div>
            )}
            {renderResults(triangleResults)}
          </div>

          {/* Border Calculator */}
          <div style={shapeCardStyle}>
            <div style={shapeHeaderStyle}>
              <h3 style={h3Style}>{t('borderTitle')}</h3>
              <div style={shapeIconStyle}>▢</div>
            </div>
            <form onSubmit={handleBorderCalculate}>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('length')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={borderInputs.length}
                    onChange={(e) => setBorderInputs({...borderInputs, length: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={borderInputs.lengthUnit}
                    onChange={(e) => setBorderInputs({...borderInputs, lengthUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('feet')}</option>
                    <option value="yards">{t('yards')}</option>
                    <option value="inches">{t('inches')}</option>
                    <option value="meters">{t('meters')}</option>
                    <option value="centimeters">{t('centimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('width')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={borderInputs.width}
                    onChange={(e) => setBorderInputs({...borderInputs, width: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={borderInputs.widthUnit}
                    onChange={(e) => setBorderInputs({...borderInputs, widthUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('feet')}</option>
                    <option value="yards">{t('yards')}</option>
                    <option value="inches">{t('inches')}</option>
                    <option value="meters">{t('meters')}</option>
                    <option value="centimeters">{t('centimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('borderWidth')}</label>
                <div style={inputRowStyle}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={borderInputs.border}
                    onChange={(e) => setBorderInputs({...borderInputs, border: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={borderInputs.borderUnit}
                    onChange={(e) => setBorderInputs({...borderInputs, borderUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="feet">{t('feet')}</option>
                    <option value="yards">{t('yards')}</option>
                    <option value="inches">{t('inches')}</option>
                    <option value="meters">{t('meters')}</option>
                    <option value="centimeters">{t('centimeters')}</option>
                  </select>
                </div>
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('quantity')}</label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={borderInputs.quantity}
                  onChange={(e) => setBorderInputs({...borderInputs, quantity: parseInt(e.target.value) || 1})}
                  style={inputStyle}
                  required
                />
              </div>
              <div style={formGroupStyle}>
                <label style={labelStyle}>{t('pricePerSqFt')}</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={borderInputs.price || ''}
                  onChange={(e) => setBorderInputs({...borderInputs, price: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                  placeholder="0.00"
                />
              </div>
              <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>
            </form>
            {renderResults(borderResults)}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight5')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('insight6')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={h3Style}>{t('detailsSubtitle1')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <h3 style={h3Style}>{t('detailsSubtitle2')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <h3 style={h3Style}>{t('detailsSubtitle3')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
        <h3 style={h3Style}>{t('detailsSubtitle4')}</h3>
        <p style={paragraphStyle}>{t('detailsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.calculatorsoup.com/calculators/construction/square-footage-calculator.php" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref1')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://maintenx.com/square-footage-definitions-and-how-to-use-them/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref2')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.omnicalculator.com/construction/square-footage" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref3')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.indeed.com/career-advice/career-development/how-to-calculate-square-feet" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref4')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.bankrate.com/real-estate/determine-square-footage-of-home/" target="_blank" rel="noopener noreferrer" style={refLinkStyle}>
              {t('ref5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}